"use client";

import React from "react";
import Link from "next/link";

const CommonEmergencies: React.FC = () => {
  return (
    <section className="w-full py-6 sm:py-8 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 space-y-6 text-center md:text-left">
        <h2
          className="
            text-2xl sm:text-3xl font-semibold text-gray-800 text-center
            pb-6 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-2
            after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]
          "
        >
          Common Dental Emergencies We Treat in Astoria
        </h2>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          At Smile4Me Dental Care, we understand that{" "}
          <Link
            href="/blog/what-qualifies-as-a-dental-emergency/"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            dental emergencies
          </Link>{" "}
          can arise out of nowhere and never happen at a convenient time.
          That&apos;s why we offer fast, effective care for a wide range of
          urgent dental issues, including:
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Severe Toothache
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          A toothache intense enough to disrupt sleep, eating, or daily activity
          is not something to push through. Severe tooth pain is often a sign of
          infection, a cracked tooth, or an exposed nerve, all of which require
          prompt treatment. Call our emergency dentist in Astoria right away so
          we can diagnose the source and relieve your pain the same day.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Knocked-Out Tooth
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          Time is everything when it comes to a knocked-out tooth. Pick it up by
          the crown, never the root, rinse it gently without scrubbing, and try
          to place it back in the socket or store it in milk. Then call us
          immediately at{" "}
          <a
            href="tel:+17184004400"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            (718) 400-4400
          </a>
          . Teeth replanted within 30&ndash;60 minutes have the best chance of
          being saved.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Cracked or Broken Tooth
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          A{" "}
          <Link
            href="/blog/is-a-cracked-tooth-an-emergency/"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            cracked or broken tooth
          </Link>{" "}
          can range from a minor chip to a fracture that exposes the inner pulp.
          Even cracks that don&apos;t hurt immediately can worsen quickly with
          chewing pressure or temperature changes. We&apos;ll assess the damage
          and determine whether bonding, a crown, or another treatment is the
          right fix.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Dental Abscess
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          An{" "}
          <Link
            href="/blog/is-a-dental-abscess-an-emergency/"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            abscess is a bacterial infection
          </Link>{" "}
          that forms at the root of a tooth or in the gum tissue. Signs include
          throbbing pain, swelling, a bad taste in your mouth, or a visible bump
          on the gum. This is a true emergency; dental infections can spread to
          the jaw, neck, and beyond if not treated promptly. Don&apos;t wait on
          this one.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Lost or Broken Crown or Filling
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          When a crown falls off or a filling breaks, the exposed tooth is
          vulnerable to pain, sensitivity, and further damage. We stock same-day
          restorations for most cases, so your tooth can be protected in a
          single visit.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Broken or Displaced Dentures or Dental Appliances
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          A broken denture, a loose implant, or a dislodged bridge affects both
          your ability to eat and your confidence. We can evaluate and repair or
          replace damaged appliances quickly so you&apos;re not left without a
          solution.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Swollen Jaw or Face
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          Facial swelling alongside tooth pain almost always signals infection.
          This is a medical and dental emergency; call us at{" "}
          <a
            href="tel:+17184004400"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            (718) 400-4400
          </a>{" "}
          immediately or go to the nearest emergency room if the swelling is
          severe or spreading rapidly.
        </p>

        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Soft Tissue Injuries
        </h3>
        <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
          Cuts, lacerations, or puncture wounds to the tongue, cheeks, lips, or
          gums should be cleaned and evaluated promptly. Apply gentle pressure
          with a clean cloth on the way to our office.
        </p>
      </div>
    </section>
  );
};

export default CommonEmergencies;
